import { Box, Input, InputAdornment, Stack, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { getAppBuilderCurrentStyleValue } from 'app/app/editor/tabs/settings/style-editor/StylesEditorContainer';
import { useAppBuilderContext } from 'app/app/editor/context';
import { getNodeByUid } from 'app/app/editor/page/getNodeByUid';
import IconMi from 'components/common/icon/IconMi';

interface SizeFieldProps {
    formState: any;
    propertyName: string;
    onChangeStyle: (value: any) => void;
    uid: string;

    type: 'side' | 'corner';
}

export default function FieldBySide({ type, propertyName, formState, onChangeStyle = () => {}, uid }: SizeFieldProps) {
    const ctx = useAppBuilderContext();
    const node = uid == 'page' ? ctx.page.layout : getNodeByUid(ctx.page.layout, uid ?? '');
    const layoutSettings = node ? (node?.params?.layout ?? {}) : {};

    const [fillTypeValue, setFillTypeValue] = useState<string>('all');
    const [fieldValue, setFieldValue] = useState<any>({
        all: '',
        top: '',
        right: '',
        bottom: '',
        left: '',
    });
    const isFocusFieldRef = useRef<boolean>(false);

    const updateValues = () => {
        if (node) {
            const currentValue = getAppBuilderCurrentStyleValue(node?.params?.layout ?? {}, propertyName);
            const parts = currentValue.split(' ');
            if (parts.length == 4) {
                setFillTypeValue('individual');
                setFieldValue({ ...fieldValue, top: parts[0], right: parts[1], bottom: parts[2], left: parts[3] });
            } else {
                setFillTypeValue('all');
                setFieldValue({ all: currentValue });
            }
        }
    };
    useEffect(() => {
        updateValues();
    }, []);

    useEffect(() => {
        if (!isFocusFieldRef.current) return;

        const formatValue = (val: any) => {
            if (val === undefined || val === null || val === '') return '0';
            const trimmed = String(val).trim();
            // If it already has a unit, keep it
            if (/^-?\d+(\.\d+)?(px|em|rem|%|vh|vw)$/.test(trimmed)) {
                return trimmed;
            }
            // If it's just a number, append px
            if (!isNaN(Number(trimmed))) {
                return `${trimmed}px`;
            }
            // Otherwise, return as is
            return trimmed;
        };

        if (fillTypeValue === 'all') {
            onChangeStyle({
                [propertyName]: formatValue(fieldValue.all),
            });
        } else {
            onChangeStyle({
                [propertyName]: [
                    formatValue(fieldValue?.top),
                    formatValue(fieldValue?.right),
                    formatValue(fieldValue?.bottom),
                    formatValue(fieldValue?.left),
                ].join(' '),
            });
        }
    }, [fillTypeValue, fieldValue]);

    useEffect(() => {
        if (!isFocusFieldRef.current) updateValues();
    }, [layoutSettings?.[propertyName]]);

    return (
        <Stack spacing={1}>
            <Stack direction={'row'} spacing={1}>
                <Box flexGrow={1}>
                    <Input
                        className={'design-variant--secondary'}
                        fullWidth
                        value={fillTypeValue == 'individual' ? 'Mixed' : fieldValue.all}
                        onChange={(e) => {
                            setFieldValue({
                                ...fieldValue,
                                all: e.target.value,
                            });
                        }}
                        placeholder={'0'}
                        disabled={fillTypeValue == 'individual'}
                        onFocus={() => {
                            isFocusFieldRef.current = true;
                        }}
                        onBlur={() => {
                            isFocusFieldRef.current = false;
                        }}
                        inputProps={{ 'data-1p-ignore': true }}
                        endAdornment={
                            fillTypeValue === 'all' ? (
                                <InputAdornment position="end" sx={{ mx: 0 }}>
                                    <Box sx={{ px: 1 }}>px</Box>
                                </InputAdornment>
                            ) : undefined
                        }
                    />
                </Box>

                <Box flexShrink={0}>
                    <ToggleButtonGroup
                        className={'design-variant--secondary'}
                        value={fillTypeValue}
                        exclusive
                        onChange={(event, value) => {
                            if (value !== null) {
                                setFillTypeValue(value);
                            }
                        }}
                        sx={{
                            svg: { width: '16px', height: '16px' },
                        }}
                    >
                        <Tooltip title={'All ' + (type == 'side' ? 'sides' : 'corners')}>
                            <ToggleButton value="all">
                                <IconMi icon={'corner-full'} />
                            </ToggleButton>
                        </Tooltip>
                        <Tooltip title={'Individual ' + (type == 'side' ? 'sides' : 'corners')}>
                            <ToggleButton value="individual">
                                <IconMi icon={'corner'} />
                            </ToggleButton>
                        </Tooltip>
                    </ToggleButtonGroup>
                </Box>
            </Stack>

            {fillTypeValue != 'all' && (
                <>
                    <Stack direction={'row'} spacing={1}>
                        <Box>
                            <Input
                                className={'design-variant--secondary'}
                                fullWidth
                                value={fieldValue.top}
                                onChange={(e) => {
                                    setFieldValue({ ...fieldValue, top: e.target.value });
                                }}
                                placeholder={'0'}
                                onFocus={() => {
                                    isFocusFieldRef.current = true;
                                }}
                                onBlur={() => {
                                    isFocusFieldRef.current = false;
                                }}
                                inputProps={{ 'data-1p-ignore': true }}
                                startAdornment={
                                    <InputAdornment position="start" sx={{ mx: 0 }}>
                                        <Box sx={{ px: 1 }}>{type == 'side' ? 'T' : 'TL'}</Box>
                                    </InputAdornment>
                                }
                                endAdornment={
                                    <InputAdornment position="end" sx={{ mx: 0 }}>
                                        <Box sx={{ px: 1 }}>px</Box>
                                    </InputAdornment>
                                }
                            />
                        </Box>
                        <Box>
                            <Input
                                className={'design-variant--secondary'}
                                fullWidth
                                value={fieldValue.right}
                                onChange={(e) => {
                                    setFieldValue({ ...fieldValue, right: e.target.value });
                                }}
                                placeholder={'0'}
                                onFocus={() => {
                                    isFocusFieldRef.current = true;
                                }}
                                onBlur={() => {
                                    isFocusFieldRef.current = false;
                                }}
                                inputProps={{ 'data-1p-ignore': true }}
                                startAdornment={
                                    <InputAdornment position="start" sx={{ mx: 0 }}>
                                        <Box sx={{ px: 1 }}>{type == 'side' ? 'R' : 'TR'}</Box>
                                    </InputAdornment>
                                }
                                endAdornment={
                                    <InputAdornment position="end" sx={{ mx: 0 }}>
                                        <Box sx={{ px: 1 }}>px</Box>
                                    </InputAdornment>
                                }
                            />
                        </Box>
                    </Stack>
                    <Stack direction={'row'} spacing={1}>
                        <Box>
                            <Input
                                fullWidth
                                value={fieldValue.bottom}
                                onChange={(e) => {
                                    setFieldValue({ ...fieldValue, bottom: e.target.value });
                                }}
                                placeholder={'0'}
                                onFocus={() => {
                                    isFocusFieldRef.current = true;
                                }}
                                onBlur={() => {
                                    isFocusFieldRef.current = false;
                                }}
                                inputProps={{ 'data-1p-ignore': true }}
                                className={'design-variant--secondary'}
                                startAdornment={
                                    <InputAdornment position="start" sx={{ mx: 0 }}>
                                        <Box sx={{ px: 1 }}>{type == 'side' ? 'B' : 'BR'}</Box>
                                    </InputAdornment>
                                }
                                endAdornment={
                                    <InputAdornment position="end" sx={{ mx: 0 }}>
                                        <Box sx={{ px: 1 }}>px</Box>
                                    </InputAdornment>
                                }
                            />
                        </Box>
                        <Box>
                            <Input
                                fullWidth
                                value={fieldValue.left}
                                onChange={(e) => {
                                    setFieldValue({ ...fieldValue, left: e.target.value });
                                }}
                                placeholder={'0'}
                                onFocus={() => {
                                    isFocusFieldRef.current = true;
                                }}
                                onBlur={() => {
                                    isFocusFieldRef.current = false;
                                }}
                                inputProps={{ 'data-1p-ignore': true }}
                                className={'design-variant--secondary'}
                                startAdornment={
                                    <InputAdornment position="start" sx={{ mx: 0 }}>
                                        <Box sx={{ px: 1 }}>{type == 'side' ? 'L' : 'BL'}</Box>
                                    </InputAdornment>
                                }
                                endAdornment={
                                    <InputAdornment position="end" sx={{ mx: 0 }}>
                                        <Box sx={{ px: 1 }}>px</Box>
                                    </InputAdornment>
                                }
                            />
                        </Box>
                    </Stack>
                </>
            )}
        </Stack>
    );
}
